import { useState, useRef, ChangeEvent } from "react";
import {
  Box,
  Text,
  Grid,
  GridItem,
  Flex,
  Button,
  Checkbox,
  Tooltip,
  IconButton,
  useToast,
} from "@chakra-ui/react";
import { ChatIcon } from "@chakra-ui/icons";
import { getStatusInfoFromApi } from "@/utils/balanceSolicitationStatusMapper";
import { documentFormatter } from "@/utils/documentFormatter";
import { format } from "date-fns";
import { handleCopy } from "@/utils/handleCopy";
import { FaRegCopy } from "react-icons/fa";
import { ConsignadoBalanceDetailModal } from "../ConsignadoBalanceDetailModal";
import {
  ConsigBalanceSolicitation,
  ConsigBalanceSolicitationStatusEnum,
} from "@/models/consignado/balanceSolicitation.model";
import { ConsignadoBalanceSolicitationAnalysisModal } from "../ConsignadoBalanceSolicitationAnalysisModal";

interface BalanceSolicitationResultCardsProps {
  data?: ConsigBalanceSolicitation[];
  selectedIds: string[];
  setSelectedIds: (ids: string[]) => void;
  isConsignado?: boolean;
}

export default function ConsignadoBalanceSolicitationResultCards({
  data = [],
  selectedIds,
  setSelectedIds,
  isConsignado,
}: BalanceSolicitationResultCardsProps) {
  const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] =
    useState<ConsigBalanceSolicitation | null>(null);
  const [selectedBalanceSolicitationId, setSelectedBalanceSolicitationId] =
    useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const formatDate = (date: string): string => {
    const dateObj = new Date(date);
    return isNaN(dateObj.getTime())
      ? "Data não disponível"
      : format(dateObj, "dd/MM/yyyy - HH:mm");
  };

  const getAnalysisButtonState = (item: ConsigBalanceSolicitation) => {
    const isWaitingAuthorization =
      item.status ===
      ConsigBalanceSolicitationStatusEnum.AGUARDANDO_AUTORIZACAO_DO_CLIENTE;

    const isProcessing =
      item.status === ConsigBalanceSolicitationStatusEnum.EM_PROCESSAMENTO;

    if (isProcessing) {
      return {
        disabled: true,
        label: "Em processamento",
        tooltip: "Consulta em processamento",
      };
    }

    const hasNotMarginMessage = item.descricao
      ?.trim()
      .includes("Margem insulficiente para um novo empréstimo");

    if (hasNotMarginMessage) {
      return {
        disabled: true,
        label: "Análise indisponível",
        tooltip: item.descricao?.trim() || "",
      };
    }

    const errorTomador =
      item.descricao?.trim() ===
        "Tomador não possui vínculos empregaticios CLT" ||
      item.descricao?.includes("Tomador não possui vínculos empregaticios CLT");

    if (errorTomador) {
      return {
        disabled: true,
        label: "Análise indisponível",
        tooltip: "Tomador não possui vínculos empregaticios CLT",
      };
    }

    const hasBmpError =
      item.descricao?.trim() === "Erro ao consultar margem na BMP" ||
      item.descricao?.includes("Erro ao consultar margem na BMP");

    if (isWaitingAuthorization) {
      return {
        disabled: true,
        label: "Aguardando autorização",
        tooltip: "Aguardando o cliente autorizar a consulta",
      };
    }

    if (hasBmpError) {
      return {
        disabled: true,
        label: "Análise indisponível",
        tooltip: "Erro ao consultar margem na BMP",
      };
    }

    return {
      disabled: false,
      label: "Análise de Crédito",
      tooltip: "",
    };
  };

  const handleCardClick = (index: number) => {
    const item = data[index];

    setSelectedItem(item);
  };

  const handleCheckboxToggle = (
    id: string,
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    event.stopPropagation();
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter((selectedId) => selectedId !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  // const handleButtonClick = (
  //   event: React.MouseEvent<HTMLButtonElement>,
  //   item: ConsigBalanceSolicitation
  // ) => {
  //   event.stopPropagation();
  //   if (item.status === ConsigBalanceSolicitationStatusEnum.CONCLUIDO) {
  //     navigate(`/simulacao/${item.id}/${item.cpf}`,{
  //       // state: {
  //       //   products: item?.products
  //       // }
  //     });
  //   }
  // };

  const toast = useToast();

  return (
    <>
      <Grid
        ref={gridRef}
        templateColumns={{
          base: "repeat(1, 1fr)",
          sm: "repeat(1, 1fr)",
          md: "repeat(2, 1fr)",
          lg: "repeat(3, 1fr)",
          xl: "repeat(4, 1fr)",
        }}
        gap={10}
        justifyContent={"center"}
        w="100%"
      >
        {data?.map((item, index) => {
          const itemStatus = getStatusInfoFromApi(item.status);
          const itemLegend = getStatusInfoFromApi(item.status);
          const tagColor = itemStatus.colorScheme;
          const isChecked = selectedIds.includes(item.id!);
          const analysisState = getAnalysisButtonState(item);

          return (
            <GridItem
              key={item.id}
              position="relative"
              id={`card-${index}`}
              colSpan={1}
              width={"90%"}
            >
              <Box
                borderWidth="1px"
                borderRadius="lg"
                overflow="hidden"
                transition="transform 0.3s ease, box-shadow 0.3s ease"
                boxShadow="md"
                position="relative"
                width="100%"
                height="100%"
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
                bg="white"
                _hover={{
                  transform: "scale(1.05)",
                  boxShadow: "lg",
                }}
                minHeight="20rem"
                minWidth={{ base: "20rem", md: "14rem" }}
              >
                <Flex
                  bg={tagColor}
                  height="3.125rem"
                  justify="space-between"
                  alignItems="center"
                  p={2}
                >
                  <Text
                    fontSize={"md"}
                    fontFamily="Lexend Deca"
                    fontWeight="bold"
                    color="white"
                  >
                    {itemStatus.label}
                  </Text>
                  <Checkbox
                    isChecked={isChecked}
                    onChange={(event) => handleCheckboxToggle(item.id!, event)}
                    colorScheme="green"
                    mr={2}
                    gap={2}
                    onClick={(event) => event.stopPropagation()}
                  />
                </Flex>
                <Box
                  p={4}
                  onClick={() => handleCardClick(index)}
                  cursor="pointer"
                  flexGrow={1}
                  display="flex"
                  flexDirection="column"
                  justifyContent="space-between"
                >
                  <Flex ml={-2} direction="column" alignItems="flex-start">
                    <Flex alignItems="center" align={"baseline"} gap={1}>
                      <Text
                        fontSize={"sm"}
                        color="gray.700"
                        fontFamily="Lexend Deca"
                      >
                        Nome:
                      </Text>
                      <Text
                        fontSize={"sm"}
                        fontFamily="Lexend Deca"
                        isTruncated
                      >
                        {item.nome_cliente}
                      </Text>
                    </Flex>
                    <Flex alignItems="center" align={"baseline"} gap={2}>
                      <Text fontSize={"sm"} color="gray.700">
                        CPF:
                      </Text>
                      <Text
                        mt={1}
                        fontSize={"sm"}
                        color="blue.600"
                        fontFamily="Lexend Deca"
                      >
                        {documentFormatter(item.cpf) ||
                          "Documento não disponível"}
                      </Text>
                    </Flex>
                  </Flex>
                  {item?.status !==
                    ConsigBalanceSolicitationStatusEnum.EM_PROCESSAMENTO && (
                    <Flex
                      ml={-2}
                      mb={4}
                      alignItems="baseline"
                      fontFamily="Lexend Deca"
                    >
                      <Tooltip label="Legenda da Consulta" placement="top">
                        <ChatIcon boxSize={3} />
                      </Tooltip>
                      <Text fontSize={"0.75rem"} color="gray.600" ml={2}>
                        {item?.descricao
                          ? item?.descricao
                          : itemLegend.tagLabel}
                      </Text>
                    </Flex>
                  )}
                  {isConsignado &&
                    item.status ==
                      ConsigBalanceSolicitationStatusEnum.AGUARDANDO_AUTORIZACAO_DO_CLIENTE && (
                      <Flex direction="column" ml={-2}>
                        <Flex
                          alignItems="center"
                          justifyContent="space-between"
                          flexWrap="nowrap"
                        >
                          <Text
                            fontSize={"sm"}
                            color="gray.700"
                            fontFamily="Lexend Deca"
                          >
                            Termo de Autorização:
                          </Text>
                          <IconButton
                            ml={2}
                            aria-label="Copiar Link de acesso ao Termo de Autorização"
                            icon={<FaRegCopy />}
                            onClick={() =>
                              handleCopy(
                                item.termo_consentimento_url ??
                                  `${window.location.origin}/consignado/autorizar-consulta`,
                                toast,
                              )
                            }
                            size="sm"
                            variant="ghost"
                            color="gray.500"
                          />
                        </Flex>
                        <Text
                          fontSize={"sm"}
                          color="blue.600"
                          fontFamily="Lexend Deca"
                          onClick={() => {
                            const linkUrl =
                              item.termo_consentimento_url ??
                              `${window.location.origin}/consignado/autorizar-consulta?balanceSolicitationId=${item.id}`;
                            window.open(
                              linkUrl,
                              "_blank",
                              "noopener,noreferrer",
                            );
                          }}
                          _hover={{
                            textDecoration: "underline",
                          }}
                          cursor="pointer"
                          maxW="100%"
                          wordBreak="break-all"
                        >
                          {item.termo_consentimento_url ??
                            `${window.location.origin}/consignado/autorizar-consulta?balanceSolicitationId=${item.id}`}
                        </Text>
                      </Flex>
                    )}
                  <Flex flexDirection="column" alignItems="center" mt={4}>
                    {/* TODO: Adicionar botão de análise de crédito */}

                    <Tooltip
                      label={analysisState.tooltip}
                      isDisabled={
                        !analysisState.disabled || !analysisState.tooltip
                      }
                      hasArrow
                    >
                      <Button
                        fontSize={"md"}
                        colorScheme="primary"
                        isDisabled={analysisState.disabled}
                        onClick={(event) => {
                          event.stopPropagation();
                          if (analysisState.disabled) return;
                          setSelectedBalanceSolicitationId(item.id || null);
                          setIsAnalysisModalOpen(true);
                        }}
                        mb={5}
                        fontFamily="Lexend Deca"
                      >
                        Análise de Crédito
                      </Button>
                    </Tooltip>
                  </Flex>
                </Box>
                <Text
                  fontSize={{
                    base: "sm",
                    md: "0.625rem",
                  }}
                  color="gray.500"
                  position="absolute"
                  bottom="0.625rem"
                  right="0.625rem"
                  fontWeight={"bold"}
                  fontFamily="Lexend Deca"
                >
                  {formatDate(item?.data_criacao?.toLocaleString("pt-BR"))}
                </Text>
              </Box>
            </GridItem>
          );
        })}
      </Grid>

      {isAnalysisModalOpen && (
        <ConsignadoBalanceSolicitationAnalysisModal
          isOpen={isAnalysisModalOpen}
          onClose={() => {
            setIsAnalysisModalOpen(false);
            setSelectedBalanceSolicitationId(null);
          }}
          balanceSolicitationId={selectedBalanceSolicitationId}
        />
      )}

      <ConsignadoBalanceDetailModal
        isOpen={!!selectedItem && !isAnalysisModalOpen}
        onClose={() => setSelectedItem(null)}
        selectedItem={selectedItem}
        formatDate={formatDate}
        isConsignado={true}
      />
    </>
  );
}
